import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Card,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@material-ui/core";
import { ExpandLess, ExpandMore } from "@material-ui/icons";

import PushNotificationService from "./Locality/Service/pushNotificationService";
import socketService from "./Locality/Service/socketService";
import PageTitle from "../../components/PageTitle/PageTitle";
import { linkifyText } from "../../utils/linkifyText";
import { useDomainFeatures } from "../../context/DomainFeaturesContext";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS_BACK = 3;

function getCurrentMonthNum() {
  return String(new Date().getMonth() + 1).padStart(2, "0");
}

/** Current year and previous 3 years (newest first). */
function buildYearOptions() {
  return Array.from({ length: YEARS_BACK + 1 }, (_, i) => CURRENT_YEAR - i);
}

/** All 12 months for a year (January → December). */
function buildMonthOptions(year) {
  return Array.from({ length: 12 }, (_, i) => {
    const value = String(i + 1).padStart(2, "0");
    const label = new Date(year, i, 1).toLocaleString(undefined, { month: "long" });
    return { value, label };
  });
}

function toMonthQuery(year, monthNum) {
  return `${year}-${monthNum}`;
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Date with month name, ordered by the domain's date format, followed by local time.
 * DD-MM-YYYY (.in) → 14 Oct 2026, 9:29:09 AM · MM-DD-YYYY (.org) → Oct 14, 2026, 9:29:09 AM
 */
function formatNotificationDateTime(value, dateFormat) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const day = d.getDate();
  const month = MONTH_NAMES[d.getMonth()];
  const year = d.getFullYear();
  let datePart;
  if (dateFormat === "MM-DD-YYYY") datePart = `${month} ${day}, ${year}`;
  else if (dateFormat === "YYYY-MM-DD") datePart = `${year} ${month} ${day}`;
  else datePart = `${day} ${month} ${year}`;
  return `${datePart}, ${d.toLocaleTimeString()}`;
}

export default function ParentNotifications() {
  const { dateFormat } = useDomainFeatures();
  const userDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetail") || "{}");
    } catch {
      return {};
    }
  }, []);

  const schooleId = useMemo(() => {
    const id = userDetails?.schooleId || userDetails?.schoolId;
    return id && typeof id === "object" ? id?._id : id;
  }, [userDetails]);

  const yearOptions = useMemo(() => buildYearOptions(), []);

  const [items, setItems] = useState([]);
  const [expandedIds, setExpandedIds] = useState({});
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [selectedMonthNum, setSelectedMonthNum] = useState(getCurrentMonthNum);
  const [listLoading, setListLoading] = useState(false);

  const monthOptions = useMemo(
    () => buildMonthOptions(selectedYear),
    [selectedYear],
  );

  const monthQuery = useMemo(
    () => toMonthQuery(selectedYear, selectedMonthNum),
    [selectedYear, selectedMonthNum],
  );

  const handleYearChange = (year) => {
    const y = Number(year);
    setSelectedYear(y);
    if (y === CURRENT_YEAR) {
      setSelectedMonthNum(getCurrentMonthNum());
    } else {
      setSelectedMonthNum((prev) => prev || "01");
    }
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    if (!schooleId) return;
    setListLoading(true);
    PushNotificationService.listParentNotifications({
      year: selectedYear,
      month: monthQuery,
    })
      .then((res) => {
        const data = res?.data || res;
        setItems(Array.isArray(data?.notifications) ? data.notifications : []);
      })
      .catch(() => {
        setItems([]);
      })
      .finally(() => setListLoading(false));
  }, [schooleId, selectedYear, monthQuery]);

  useEffect(() => {
    // Real-time updates: when school admin sends, parent sees instantly.
    socketService.joinPushNotifications({
      onNotification: (notif) => {
        setItems((prev) => {
          if (!notif || !notif._id) return prev;
          if (prev.some((x) => x._id === notif._id)) return prev;
          try {
            const raw = localStorage.getItem("parent_notifications_unread") || "0";
            const n = Number(raw);
            const next = (Number.isFinite(n) ? n : 0) + 1;
            localStorage.setItem("parent_notifications_unread", String(next));
            window.dispatchEvent(new Event("parent-notifications-unread-changed"));
          } catch {
            // ignore storage errors
          }
          return [notif, ...prev];
        });
      },
    });

    return () => {
      const socket = socketService.getSocket();
      if (socket) {
        socket.off('push-notification');
      }
    };
  }, []);

  useEffect(() => {
    // Mark as read when opening notifications page
    try {
      localStorage.setItem("parent_notifications_unread", "0");
      window.dispatchEvent(new Event("parent-notifications-unread-changed"));
    } catch {
      // ignore
    }
  }, []);

  return (
    <Container maxWidth="md">
      <PageTitle title="Notifications" />
      <Card style={{ padding: 18, borderRadius: 14, border: "1px solid #d9d9d9" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={12}>
          <Box>
            <Typography variant="h6" style={{ fontWeight: 800, color: "#0F3B62" }}>
              School Notifications
            </Typography>
            <Typography variant="body2" style={{ color: "#555", marginTop: 2 }}>
              Important updates from your school admin.
            </Typography>
          </Box>
          <Chip
            label={`${items.length} ${items.length === 1 ? "item" : "items"}`}
            style={{ backgroundColor: "#EEF6FF", color: "#0F3B62", fontWeight: 700 }}
          />
        </Box>

        <Box mt={2}>
          <Box mb={2} display="flex" justifyContent="flex-end" flexWrap="wrap" gridGap={12}>
            <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
              <InputLabel id="notification-year-filter-label">Year</InputLabel>
              <Select
                labelId="notification-year-filter-label"
                value={selectedYear}
                onChange={(e) => handleYearChange(e.target.value)}
                label="Year"
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
              <InputLabel id="notification-month-filter-label">Month</InputLabel>
              <Select
                labelId="notification-month-filter-label"
                value={selectedMonthNum}
                onChange={(e) => setSelectedMonthNum(e.target.value)}
                label="Month"
              >
                {monthOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {listLoading ? (
            <Typography variant="body2" style={{ color: "#555" }}>
              Loading notifications…
            </Typography>
          ) : items.length === 0 ? (
            <Paper
              elevation={0}
              style={{
                padding: 18,
                borderRadius: 14,
                border: "1px dashed #cbd5e1",
                background: "linear-gradient(180deg, #ffffff 0%, #fafafa 100%)",
              }}
            >
              <Typography variant="subtitle1" style={{ fontWeight: 800, color: "#0F3B62" }}>
                No notifications for selected month
              </Typography>
              <Typography variant="body2" style={{ color: "#555", marginTop: 6 }}>
                Try selecting a different month or year to view notifications.
              </Typography>
            </Paper>
          ) : (
            items.map((n) => (
              (() => {
                const isExpanded = Boolean(expandedIds[n._id]);
                const previewText =
                  n.message && n.message.length > 220
                    ? `${n.message.slice(0, 220)}...`
                    : n.message;
                const attachments = Array.isArray(n.attachments) ? n.attachments : [];
                const legacyImageUrls = Array.isArray(n.imageUrls) ? n.imageUrls : [];
                const allAttachments =
                  attachments.length > 0
                    ? attachments
                    : legacyImageUrls.map((url) => ({ url, name: "image", mime: "image/*" }));
                const imageAttachments = allAttachments.filter((a) =>
                  String(a.mime || "").startsWith("image/"),
                );
                const fileAttachments = allAttachments.filter(
                  (a) => !String(a.mime || "").startsWith("image/"),
                );

                return (
                  <Paper
                    key={n._id}
                    elevation={0}
                    style={{
                      padding: 16,
                      borderRadius: 14,
                      border: "1px solid #d9d9d9",
                      marginBottom: 14,
                      background: "linear-gradient(180deg, #ffffff 0%, #fafafa 100%)",
                    }}
                  >
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" gridGap={10}>
                      <Box flex={1} minWidth={0}>
                        <Typography variant="subtitle1" style={{ fontWeight: 900, color: "#0F3B62" }}>
                          {n.subject || "Notification"}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gridGap={8}>
                        <Chip
                          size="small"
                          label={formatNotificationDateTime(n.createdAt, dateFormat)}
                          style={{ backgroundColor: "#F1F5F9", color: "#444" }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => toggleExpanded(n._id)}
                          style={{ color: "#0F3B62" }}
                        >
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Box>
                    </Box>

                    <Divider style={{ marginTop: 10, marginBottom: 12 }} />

                    <Typography
                      variant="body2"
                      style={{
                        color: "#333",
                        lineHeight: 1.75,
                        whiteSpace: "pre-wrap",
                        fontSize: 14,
                      }}
                    >
                      {linkifyText(isExpanded ? n.message : previewText)}
                    </Typography>

                    {(imageAttachments.length > 0 || fileAttachments.length > 0) && (
                      <Box mt={2}>
                        {fileAttachments.length > 0 && (
                          <Box mb={1}>
                            {fileAttachments.map((a, index) => (
                              <Box
                                key={`${n._id}-file-${index}`}
                                mb={1}
                                p={1.25}
                                style={{
                                  border: "1px solid #d9d9d9",
                                  borderRadius: 10,
                                  background: "#ffffff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 12,
                                }}
                              >
                                <Box minWidth={0}>
                                  <Typography
                                    variant="body2"
                                    style={{
                                      color: "#0F3B62",
                                      fontWeight: 800,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                    title={a.name}
                                  >
                                    {a.name}
                                  </Typography>
                                  <Typography variant="caption" style={{ color: "#555" }}>
                                    {a.mime || "file"}
                                  </Typography>
                                </Box>
                                <a href={a.url} target="_blank" rel="noopener noreferrer" style={{ color: "#0b5cc2", fontWeight: 700 }}>
                                  Download
                                </a>
                              </Box>
                            ))}
                          </Box>
                        )}
                        <Grid container spacing={2}>
                          {imageAttachments.map((a, index) => (
                            <Grid item xs={6} sm={4} key={`${n._id}-img-${index}`}>
                              <a href={a.url} target="_blank" rel="noopener noreferrer">
                                <img
                                  src={a.url}
                                  alt={`notification-${index + 1}`}
                                  style={{
                                    width: "100%",
                                    height: isExpanded ? 150 : 110,
                                    objectFit: "cover",
                                    borderRadius: 10,
                                    border: "1px solid #d9d9d9",
                                  }}
                                />
                              </a>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}

                    <Box mt={1} display="flex" justifyContent="flex-end">
                      <Typography
                        variant="caption"
                        style={{ color: "#0b5cc2", fontWeight: 700, cursor: "pointer" }}
                        onClick={() => toggleExpanded(n._id)}
                      >
                        {isExpanded ? "Collapse" : "Expand"}
                      </Typography>
                    </Box>

                    {isExpanded && (
                      <Box mt={2} pt={2} style={{ borderTop: "1px solid #d9d9d9" }}>
                        <Typography variant="caption" style={{ color: "#555", fontWeight: 700 }}>
                          Thanks & Regards,
                        </Typography>
                        <Typography variant="body2" style={{ color: "#0F3B62", fontWeight: 800, marginTop: 2, whiteSpace: "pre-wrap" }}>
                          {linkifyText(n.thanksAndRegards)}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                );
              })()
            ))
          )}
        </Box>
      </Card>
    </Container>
  );
}

